/**
 * Battle-royale round pacing.
 *
 * One round is roughly thirty seconds and has to take a full field - up to 194
 * flags - down to exactly one. The whole character of the format lives in *when*
 * those eliminations land.
 *
 * A flat rate would clear 194 flags at a steady seven per second and the round
 * would be over emotionally long before it is over numerically. So the schedule
 * is deliberately lopsided:
 *
 *   - the opening seconds are carnage, dozens of flags at a time;
 *   - the middle thins out;
 *   - the last handful go one at a time, with a real beat between them.
 *
 * That final stretch is the entire point. It is where a viewer picks a flag to
 * root for.
 */
import type { RoyalePhaseId, PhaseDefinition } from './types.js';

export const ROYALE_PHASES: readonly PhaseDefinition[] = [
  {
    id: 'royale_qualifying',
    order: 1,
    kind: 'royale',
    label: 'Qualifying',
    shortLabel: 'QUALIFYING',
    weight: 1,
    entrants: 0,
    survivors: 0,
  },
  {
    id: 'royale_final',
    order: 2,
    kind: 'royale',
    label: 'Last Flag Standing',
    shortLabel: 'LAST FLAG STANDING',
    weight: 1,
    entrants: 0,
    survivors: 1,
  },
  {
    id: 'royale_champion',
    order: 3,
    kind: 'ceremony',
    label: 'Champion',
    shortLabel: 'CHAMPION',
    weight: 1,
    entrants: 1,
    survivors: 1,
  },
  {
    id: 'royale_intermission',
    order: 4,
    kind: 'ceremony',
    label: 'Next Tournament',
    shortLabel: 'NEXT TOURNAMENT',
    weight: 1,
    entrants: 0,
    survivors: 0,
  },
];

const ROYALE_BY_ID = new Map(ROYALE_PHASES.map((p) => [p.id, p]));

export function royalePhaseDefinition(id: RoyalePhaseId): PhaseDefinition {
  const def = ROYALE_BY_ID.get(id);
  if (!def) throw new Error(`Unknown royale phase: ${id}`);
  return def;
}

export function isRoyalePhase(id: string): id is RoyalePhaseId {
  return ROYALE_BY_ID.has(id as RoyalePhaseId);
}

export interface RoundShape {
  /** Flags fly in and settle. No eliminations yet. */
  readonly introMs: number;
  /** The cull. */
  readonly battleMs: number;
  /** Winner spotlight before the next round begins. */
  readonly spotlightMs: number;
  readonly totalMs: number;
}

/**
 * Split a round's budget three ways.
 *
 * The intro and the spotlight are clamped rather than proportional: at any round
 * length the winner needs a couple of seconds of glory, and below about a second
 * the fly-in reads as a glitch rather than an entrance.
 */
export function roundShape(totalMs: number): RoundShape {
  const introMs = clampRange(totalMs * 0.09, 900, 3000);
  const spotlightMs = clampRange(totalMs * 0.16, 2200, 6000);
  const battleMs = Math.max(1500, totalMs - introMs - spotlightMs);
  return { introMs, battleMs, spotlightMs, totalMs };
}

/**
 * When each elimination lands, in ms from the start of the battle window.
 *
 * `t = battleMs * (i / eliminations) ^ SPREAD`, with `SPREAD > 1` pushing early
 * indices toward zero — so the first hundred flags are gone in the first fifth
 * of the window and the last few are spread across the rest.
 *
 * Returns exactly `flagCount - 1` timestamps, ascending: one flag always
 * survives, and it survives by construction rather than by rounding luck.
 */
export function eliminationSchedule(flagCount: number, battleMs: number): number[] {
  const eliminations = Math.max(0, flagCount - 1);
  if (eliminations === 0) return [];

  const SPREAD = 2.4;
  const times: number[] = [];

  for (let i = 1; i <= eliminations; i++) {
    const progress = i / eliminations;
    times.push(battleMs * Math.pow(progress, SPREAD));
  }

  return times;
}

/**
 * How many flags should still be alive at `elapsed` ms into the battle window.
 *
 * The inverse of the schedule above, used to drive the "168 / 168 FLAGS" bar
 * smoothly between ticks instead of stepping it on every knockout packet.
 */
export function expectedAliveAt(flagCount: number, battleMs: number, elapsed: number): number {
  if (flagCount <= 1 || battleMs <= 0) return Math.max(1, flagCount);
  const eliminations = flagCount - 1;
  const t = clampRange(elapsed / battleMs, 0, 1);
  const done = Math.floor(eliminations * Math.pow(t, 1 / 2.4));
  return Math.max(1, flagCount - done);
}

function clampRange(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}
